// AdminAgentDetail — admin's detail page for managing one agent.
//
// Entity Redesign Step 5 (2026-05-16): rebuilt around the same
// visual vocabulary as AdminUserDetail — pinned collapsible identity
// header + EntityActionSheet kebab + six coherent tabs:
//
//   Overview              — single-fetch dashboard (downline-aggregate
//                            tiles + sessions count + audit count)
//   Identity & Geography  — profile, UAID, status / identity lock,
//                            AgentLocationEditor
//   Money                 — pool wallet (DirectWalletAdjust + topup +
//                            withdraw + ledger), deposit wallet, payment
//                            config
//   Partnership & Perms   — PartnershipMatrix + PermissionsMatrix +
//                            IdentityLockFieldsMatrix + rate-history
//                            timeline
//   Downline              — downline-aggregate strip + depth=2 tree
//                            with drill-in links
//   Operations & Audit    — sessions, audit, owned-players, dangerous
//                            actions (delete)
//
// The kebab opens the AgentActionSheet which covers every Tier-1 verb
// (force logout, clear lockout, reset password, toggle accepting flags,
// mark test, reassign parent, delete). Reassign Parent strictly uses
// the unified AgentReassignSheet with `preview` enabled.

import { useCallback, useEffect, useState } from 'react';
import { levelName } from '../lib/levelNames';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import AdminLayout from '@baaligames/shared-ui/admin/components/AdminLayout';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
import { SegmentedTabs, type TabDef } from '@baaligames/shared-ui/admin/components/TabHub';
import { adminApi, getAdminAccessToken } from '@baaligames/shared-ui/admin/lib/adminApi';
import { compressForUpload } from '@baaligames/shared-ui/lib/imageCompress';
import { INPUT_CLS } from '../components/fieldStyles';
import LocationCapture, { type GeoBag } from '@baaligames/shared-ui/components/LocationCapture';
// H7 (2026-07-03): the fixed Pioneer Split is gone — the Partnership tab
// now hosts the per-agent PartnershipEditor (win/loss %, golden-limit).
import PartnershipEditor from '@baaligames/shared-ui/admin/components/PartnershipEditor';
import SportRatesEditor from '@baaligames/shared-ui/admin/components/SportRatesEditor';
import DepositLedgerTab from '../panels/people/DepositLedgerTab';
import AvatarCropper from '@baaligames/shared-ui/components/AvatarCropper';
import AssignPlayersToAgentModal from '../components/AssignPlayersToAgentModal';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import EntityActionSheet, { type ActionGroup } from '../components/EntityActionSheet';
import ResetPasswordModal from '../components/ResetPasswordModal';
import DirectWalletAdjustModal from '../components/DirectWalletAdjustModal';
import AgentReassignSheet from '../components/AgentReassignSheet';

// ─── Tab vocabulary ────────────────────────────────────────────────
type AgentTab = 'overview' | 'identity' | 'money' | 'partnership' | 'downline' | 'ops';
const AGENT_TABS: TabDef[] = [
  { key: 'overview',    label: 'Overview',     icon: 'dashboard' },
  { key: 'identity',    label: 'Profile',      icon: 'badge' },
  { key: 'money',       label: 'Money',        icon: 'account_balance_wallet' },
  { key: 'partnership', label: 'Partnership',  icon: 'account_tree' },
  { key: 'downline',    label: 'Downline',     icon: 'family_history' },
  { key: 'ops',         label: 'Manage',       icon: 'shield' },
];

// ─── Types ─────────────────────────────────────────────────────────
type Agent = {
  id: number;
  uaid: string | null;
  username: string;
  email: string;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  birthdate: string | null;
  gender: 'male' | 'female' | 'other' | 'prefer_not' | null;
  avatar_url: string | null;
  role?: 'admin' | 'agent';
  status: 'active' | 'suspended';
  parent_admin_id: number | null;
  parent_agent_id: number | null;
  parent_username?: string | null;
  level: number;
  commission_terms: Record<string, unknown>;
  permissions: Record<string, 'none' | 'view' | 'edit'>;
  wallet_balance: number;
  deposit_balance: number;
  security_deposit_target_points: number;
  owned_players: number;
  created_at: string;
  last_login_at: string | null;
  last_login_ip?: string | null;
  failed_login_attempts: number;
  locked_until: string | null;
  identity_locked: 0 | 1 | boolean;
  identity_locked_at: string | null;
  identity_locked_fields?: {
    username?: 'none'|'view'|'edit';
    email?: 'none'|'view'|'edit';
    avatar?: 'none'|'view'|'edit';
    password?: 'none'|'view'|'edit';
  } | null;
  is_accepting_players: 0 | 1 | boolean;
  is_accepting_agents: 0 | 1 | boolean;
  is_test?: 0 | 1 | boolean;
  suspended_at?: string | null;
  lock_reason?: string | null;
  latitude:       string | number | null;
  longitude:      string | number | null;
  state_id:       number | null;
  district_id:    number | null;
  area_id:        number | null;
  pincode_id?:    number | null;
  pincode?:       string | null;
  nation:         string | null;
  geo_updated_at: string | null;
  language?: string | null;
  secondary_language?: string | null;
};

type PaymentConfig = {
  upi_id: string | null;
  qr_image_url: string | null;
  whatsapp_number: string | null;
  support_phone: string | null;
  display_note: string | null;
  is_active: number;
  subagent_upi_id:          string | null;
  subagent_qr_image_url:    string | null;
  subagent_whatsapp_number: string | null;
  subagent_support_phone:   string | null;
  subagent_display_note:    string | null;
  updated_at: string;
} | null;

type LedgerRow = {
  id: number; admin_id?: number | null; kind: string; amount: number;
  source_user_id: number | null; source_ref: string | null;
  note: string | null; balance_after: number | null;
  actor_admin_id: number | null; created_at: string;
};

type PlayerRow = {
  id: number; username: string; first_name?: string | null; last_name?: string | null;
  email: string; phone: string;
  status: string; created_at: string; wallet_balance: number;
};

type PermSchema = {
  keys: string[];
  levels: Record<string, string[]>;
  defaults: Record<string, 'none' | 'view' | 'edit'>;
};

// Tier-2 endpoint shapes.
type DownlineAggregate = {
  total_descendants: number;
  active_descendants: number;
  max_depth: number;
  total_players: number;
  active_players: number;
  total_pool_balance: number;
};
type DownlineNode = {
  id: number; uaid: string | null; username: string; email: string; phone: string | null;
  status: 'active' | 'suspended'; level: number;
  parent_agent_id: number | null;
  is_test: 0 | 1 | boolean;
  is_accepting_players: 0 | 1;
  is_accepting_agents: 0 | 1;
  last_login_at: string | null;
  pool_balance: number;
  active_player_count: number;
  child_agent_count: number;
  depth: number;
};
type SessionRow = {
  id: number; user_agent: string | null; ip: string | null;
  expires_at: string; revoked_at: string | null; created_at: string;
  is_active: number | boolean;
};
type AuditEntry = {
  id: number; event: string; meta: Record<string, unknown> | string | null;
  ip: string | null; created_at: string;
  actor_id: number | null;
  actor_username: string | null;
  actor_role: 'admin' | 'agent' | null;
};
type RateHistoryRow = {
  id: number; agent_id: number;
  pillar: string; game_id: string | null;
  old_rate_pct: number; new_rate_pct: number;
  is_cascade: 0 | 1 | boolean;
  changed_by_admin_id: number | null;
  changed_by_agent_id: number | null;
  reason: string | null;
  created_at: string;
};

// ─── Constants ─────────────────────────────────────────────────────
const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

const STATUS_PILL: Record<Agent['status'], string> = {
  active:    'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
  suspended: 'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200/60 dark:bg-orange-400/10 dark:text-orange-300 dark:ring-orange-400/30',
};

const KIND_LABEL: Record<string, { label: string; tone: 'pos'|'neg'|'neutral'; icon: string }> = {
  topup_from_admin:    { label: 'Topup from admin',   tone: 'pos',     icon: 'add_circle' },
  topup_to_player:     { label: 'Topup to player',    tone: 'neg',     icon: 'send' },
  topup_from_player:   { label: 'Player recharge',    tone: 'pos',     icon: 'payments' },
  revenue_share:       { label: 'Revenue share',      tone: 'pos',     icon: 'pie_chart' },
  co_fund_offer:       { label: 'Co-fund offer',      tone: 'neg',     icon: 'redeem' },
  admin_adjust:        { label: 'Admin adjustment',   tone: 'neutral', icon: 'settings' },
  withdraw_to_admin:   { label: 'Withdraw to admin',  tone: 'neg',     icon: 'remove_circle' },
};

// ═══════════════════════════════════════════════════════════════════
//  Main component
// ═══════════════════════════════════════════════════════════════════

export default function AdminAgentDetail() {
  const { id } = useParams<{ id: string }>();
  const agentId = Number(id);
  const nav = useNavigate();
  const { admin: me, refresh: refreshSelf } = useAdminAuth();
  const isSelf = me && Number(me.id) === agentId;
  const isAdmin = me?.role === 'admin';

  const [sa, setSa]                       = useState<Agent | null>(null);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig>(null);
  const [ledger, setLedger]               = useState<{ rows: LedgerRow[]; total: number; balance: number } | null>(null);
  const [players, setPlayers]             = useState<{ rows: PlayerRow[]; total: number } | null>(null);
  const [schema, setSchema]               = useState<PermSchema | null>(null);

  const [tab, setTab]           = useState<AgentTab>('overview');
  const [err, setErr]           = useState<string | null>(null);
  const [toast, setToast]       = useState<string | null>(null);
  const [busy, setBusy]         = useState(false);
  const [cropping, setCropping] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [adjusting, setAdjusting]     = useState(false);
  const [reassigning, setReassigning] = useState(false);

  // Per-tab dirty tracking — Partnership tab houses Permissions /
  // Field-locks matrices that stage-then-commit; we need to confirm
  // before abandoning a dirty tab.
  const [dirtyTabs, setDirtyTabs] = useState<Partial<Record<AgentTab, boolean>>>({});
  const setTabDirty = useCallback((key: AgentTab, dirty: boolean) => {
    setDirtyTabs((prev) => prev[key] === dirty ? prev : { ...prev, [key]: dirty });
  }, []);
  function attemptTabChange(next: AgentTab) {
    if (next === tab) return;
    if (dirtyTabs[tab]) {
      const ok = window.confirm(
        `You have unsaved changes on the "${AGENT_TABS.find(t => t.key === tab)?.label}" tab. Switch and discard them?`
      );
      if (!ok) return;
      setTabDirty(tab, false);
    }
    setTab(next);
  }

  function note(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function uploadAgentAvatar(rawFile: File) {
    if (!sa) return;
    try {
      // 2026-05-20 — Stage 2 fast-follow: client-side compression
      // wrapping every File-input upload. Skipped if the cropped
      // output is already small (<200KB), otherwise downsized via
      // browser-image-compression in front of the server pipeline.
      const file = await compressForUpload(rawFile, { preset: 'avatar' });
      const fd = new FormData();
      fd.append('image', file);
      const t = getAdminAccessToken();
      const res = await fetch(`/api/admin/agents/${sa.id}/avatar`, {
        method: 'POST', credentials: 'include',
        headers: t ? { authorization: `Bearer ${t}` } : {},
        body: fd,
      });
      const j = await res.json();
      if (!j.ok) throw new Error(j.error?.message || 'Upload failed');
      note('Photo updated');
      await load();
    } catch (e) {
      alert((e as Error).message);
      throw e;
    }
  }

  const load = useCallback(async () => {
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        adminApi.get<{ agent: Agent; payment_config: PaymentConfig }>(`/admin/agents/${agentId}`),
        adminApi.get<{ rows: LedgerRow[]; total: number; balance: number }>(`/admin/agents/${agentId}/wallet/ledger?limit=20`),
        adminApi.get<{ rows: PlayerRow[]; total: number }>(`/admin/agents/${agentId}/players?limit=20`),
        adminApi.get<PermSchema>(`/admin/agents/permissions/schema`),
      ]);
      setSa(r1.agent);
      setPaymentConfig(r1.payment_config);
      setLedger(r2);
      setPlayers(r3);
      setSchema(r4);
    } catch (e) { setErr((e as ApiError).message); }
  }, [agentId]);

  useEffect(() => { void load(); }, [load]);

  // Generic Tier-1 verb caller (used by AgentActionSheet).
  const mutate = useCallback(async (path: string, body: unknown, message: string) => {
    try {
      await adminApi.post(path, body);
      note(message);
      setActionsOpen(false);
      await load();
    } catch (e) { setErr((e as ApiError).message); }
  }, [load]);

  const onDelete = useCallback(async () => {
    if (!sa) return;
    try {
      await adminApi.del(`/admin/agents/${sa.id}`);
      nav('/people/agents');
    } catch (e) { setErr((e as ApiError).message); }
  }, [sa, nav]);

  if (!sa) {
    return (
      <AdminLayout title="Agent" titleIcon="support_agent">
        {err ? <Banner tone="error">{err}</Banner>
             : <div className="text-center py-8 text-arena-400 dark:text-neon-200/70">Loading…</div>}
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={`@${sa.username}`}
      titleIcon="support_agent"
      subtitle={isSelf ? 'You (self-view)' : `Agent #${sa.id} · ${levelName(sa.level)}`}
    >
      {/* Pinned identity header — sticky, collapsible visual weight */}
      <PinnedHeader
        agent={sa}
        canEditAvatar={!!isAdmin}
        onPhotoEdit={() => setCropping(true)}
        onOpenActions={() => setActionsOpen(true)}
        onToggleAccepting={async (kind, next) => {
          try {
            await adminApi.patch(`/admin/agents/${sa.id}`,
              kind === 'players'
                ? { is_accepting_players: next }
                : { is_accepting_agents:  next });
            note(next ? `Now accepting ${kind}` : `Paused new ${kind}`);
            await load();
          } catch (e) { setErr((e as ApiError).message); }
        }}
        onCopyUaid={() => {
          if (!sa.uaid) return;
          try { navigator.clipboard.writeText(sa.uaid); note('UAID copied'); }
          catch { /* clipboard unavailable */ }
        }}
      />

      {/* Inline banners */}
      <div className="mt-3 space-y-2">
        {err   && <Banner tone="error">{err}</Banner>}
        {toast && <Banner tone="success">{toast}</Banner>}
      </div>

      {/* Tab strip */}
      <div className="mt-3">
        <SegmentedTabs
          tabs={AGENT_TABS}
          activeKey={tab}
          onTabChange={(k) => attemptTabChange(k as AgentTab)}
        />
      </div>

      {/* Tab body */}
      <div className="mt-3 space-y-3">
        {tab === 'overview' && (
          <OverviewTab
            agentId={sa.id} agent={sa}
            onAdjust={() => setAdjusting(true)}
            onOpenMoney={() => setTab('money')}
            onOpenDownline={() => setTab('downline')}
          />
        )}
        {tab === 'identity' && (
          <IdentityGeoTab
            sa={sa} isAdmin={!!isAdmin} busy={busy} setBusy={setBusy}
            setErr={setErr} note={note} reload={load}
          />
        )}
        {tab === 'money' && (
          <MoneyTab
            sa={sa} isAdmin={!!isAdmin}
            paymentConfig={paymentConfig}
            ledger={ledger}
            onAdjust={() => setAdjusting(true)}
            reload={load}
            note={note}
          />
        )}
        {tab === 'partnership' && (
          <PartnershipPermsTab
            sa={sa} isAdmin={!!isAdmin} schema={schema}
            onPermissionsSaved={async () => {
              note('Permissions saved');
              await load();
              if (isSelf) await refreshSelf();
            }}
            onLocksSaved={load}
            onPartnershipSaved={() => { note('Partnership terms saved'); void load(); }}
            setTabDirty={(d) => setTabDirty('partnership', d)}
            setErr={setErr}
          />
        )}
        {tab === 'downline' && (
          <DownlineTab agentId={sa.id} />
        )}
        {tab === 'ops' && (
          <OpsAuditTab
            sa={sa} isAdmin={!!isAdmin}
            players={players}
            onAssignPlayers={() => setAssigning(true)}
            mutate={mutate}
            onDelete={onDelete}
            reload={load}
            note={note}
            setErr={setErr}
          />
        )}
      </div>

      {/* ── Modals ────────────────────────────────────────────────── */}
      {cropping && isAdmin && (
        <AvatarCropper
          title={`Adjust photo for @${sa.username}`}
          onUpload={uploadAgentAvatar}
          onClose={() => setCropping(false)}
        />
      )}

      {resettingPw && isAdmin && (
        <ResetPasswordModal
          agentId={sa.id}
          username={sa.username}
          onClose={() => setResettingPw(false)}
          onDone={() => { setResettingPw(false); note(`Password reset for @${sa.username} — signed out everywhere.`); }}
        />
      )}

      {assigning && isAdmin && (
        <AssignPlayersToAgentModal
          agentId={sa.id}
          agentLabel={sa.username}
          onClose={() => setAssigning(false)}
          onDone={(n) => {
            setAssigning(false);
            note(`Assigned ${n} player${n === 1 ? '' : 's'} to @${sa.username}.`);
            void load();
          }}
        />
      )}

      {adjusting && (
        <DirectWalletAdjustModal
          subject={{
            kind: 'agent', id: sa.id, username: sa.username,
            avatarUrl: sa.avatar_url,
            level: sa.level,
            uaid: sa.uaid,
            parentUsername: sa.parent_username ?? null,
            parentAgentId: sa.parent_agent_id ?? null,
            poolBalance: sa.wallet_balance,
          }}
          onClose={() => setAdjusting(false)}
          onDone={() => { setAdjusting(false); note('Pool adjusted'); void load(); }}
        />
      )}

      {reassigning && (
        <AgentReassignSheet
          subject={{ kind: 'agent', label: sa.username, currentParentId: sa.parent_agent_id }}
          excludeAgentIds={[sa.id]}
          detach={{
            label: 'Detach to admin direct',
            description: 'Removes parent agent. Agent reports directly to admin.',
            icon: 'shield',
          }}
          preview={{ agentId: sa.id }}
          onClose={() => setReassigning(false)}
          onAssign={async (newParentId, reason) => {
            await adminApi.post(`/admin/agents/${sa.id}/reassign-parent`, {
              new_parent_agent_id: newParentId,
              reason: reason || undefined,
            });
            setReassigning(false);
            note('Parent reassigned');
            await load();
          }}
        />
      )}

      {actionsOpen && (
        <AgentActionSheet
          a={sa}
          isAdmin={!!isAdmin}
          onClose={() => setActionsOpen(false)}
          onAdjust={() => { setActionsOpen(false); setAdjusting(true); }}
          onReassign={() => { setActionsOpen(false); setReassigning(true); }}
          onResetPassword={() => { setActionsOpen(false); setResettingPw(true); }}
          onMutate={mutate}
          onDelete={onDelete}
        />
      )}
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  PinnedHeader — sticky identity card
// ═══════════════════════════════════════════════════════════════════

function PinnedHeader({
  agent: a, canEditAvatar, onPhotoEdit, onOpenActions, onToggleAccepting, onCopyUaid,
}: {
  agent: Agent;
  canEditAvatar: boolean;
  onPhotoEdit: () => void;
  onOpenActions: () => void;
  onToggleAccepting: (kind: 'players' | 'agents', next: boolean) => Promise<void>;
  onCopyUaid: () => void;
}) {
  const isActive    = a.status === 'active';
  const isLocked    = !!a.locked_until && new Date(a.locked_until) > new Date();
  const isTest      = !!a.is_test;
  const idLocked    = !!a.identity_locked;
  const depTarget   = Number(a.security_deposit_target_points || 0);
  const depBal      = Number(a.deposit_balance || 0);
  const depPct      = depTarget > 0 ? Math.min(100, Math.round((depBal / depTarget) * 100)) : null;
  const depShort    = depTarget > 0 && depBal < depTarget;
  const wallet      = Number(a.wallet_balance || 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-30 -mx-2 lg:-mx-4 px-2 lg:px-4 py-2
                 bg-neutral-50/85 dark:bg-arena-900/85 backdrop-blur
                 supports-backdrop-blur:bg-neutral-50/70
                 supports-backdrop-blur:dark:bg-arena-900/70">
      <div className="rounded-2xl p-3 sm:p-4
                      bg-white dark:bg-arena-700/40
                      ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40
                      shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                      relative">
        {/* Kebab */}
        <button
          onClick={onOpenActions}
          className="absolute top-2 right-2 grid place-items-center w-9 h-9 rounded-full
                     text-arena-500 dark:text-neon-200/65 hover:bg-arena-100/60 dark:hover:bg-arena-700/60
                     active:bg-arena-200/60 dark:active:bg-arena-700"
          aria-label="More actions">
          <Icon name="more_horiz" size={18} />
        </button>

        {/* Identity row */}
        <div className="flex items-start gap-3 pr-10">
          <div className="relative shrink-0">
            <AdminAvatar url={a.avatar_url} username={a.username} size="lg" />
            {canEditAvatar && (
              <button onClick={onPhotoEdit} title="Change photo"
                className="absolute -bottom-1 -right-1 grid place-items-center w-7 h-7 rounded-full
                           bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                           text-white shadow-md focus:outline-none focus:ring-2 focus:ring-violet-400/60 transition active:scale-95">
                <Icon name="photo_camera" size={14} />
              </button>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h2 className="text-[15px] sm:text-[17px] font-display font-extrabold text-neutral-900 dark:text-white truncate leading-tight">
                @{a.username}
              </h2>
              <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap
                               bg-violet-600 text-white shadow-[0_1px_3px_rgba(139,92,246,0.4)]">
                Agent
              </span>
              <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold leading-none
                               bg-violet-100 text-violet-700
                               dark:bg-violet-500/15 dark:text-violet-300">
                {levelName(a.level || 1)}
              </span>
              <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold capitalize ${STATUS_PILL[a.status]}`}>
                <span className="w-1 h-1 rounded-full bg-current" />
                {a.status}
              </span>
            </div>

            {/* UAID + parent + id */}
            <div className="text-[11.5px] text-arena-600 dark:text-neon-200/70 truncate mt-0.5 flex items-center gap-1.5">
              {a.uaid && (
                <button onClick={onCopyUaid}
                  className="font-mono tabular-nums tracking-[.14em] text-cyan-700 dark:text-cyan-300 hover:underline inline-flex items-center gap-1"
                  title="Copy UAID">
                  {a.uaid}
                  <Icon name="content_copy" size={10} className="opacity-60" />
                </button>
              )}
              {a.parent_username
                ? <span className="text-arena-400 dark:text-neon-200/70 truncate">under @{a.parent_username}</span>
                : <span className="italic text-arena-400 dark:text-neon-200/45">admin-direct</span>}
            </div>

            {/* Phone — tap to Call / WhatsApp / Telegram / copy */}
            {a.phone && (
              <div className="mt-0.5">
                <PhoneContact phone={a.phone} name={a.username} iconSize={11}
                  className="text-[11.5px] text-arena-700 dark:text-neon-200/80 tabular-nums hover:text-arena-900 dark:hover:text-neon-200" />
              </div>
            )}

            {/* Chip row — flags + verification + lock signals */}
            <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[10px]">
              {isTest && (
                <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                                 bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/70
                                 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30">
                  <Icon name="science" size={9} filled /> test
                </span>
              )}
              {isLocked && (
                <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                                 bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200/60
                                 dark:bg-orange-400/15 dark:text-orange-300 dark:ring-orange-400/30">
                  <Icon name="lock_clock" size={9} filled /> locked out
                </span>
              )}
              {idLocked && (
                <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                                 bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-200/60
                                 dark:bg-cyan-400/15 dark:text-cyan-300 dark:ring-cyan-400/30">
                  <Icon name="lock" size={9} filled /> id locked
                </span>
              )}
              {!isActive && (
                <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                                 bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60
                                 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30">
                  <Icon name="block" size={9} filled /> suspended
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Money strip — wallet + deposit progress */}
        <div className="mt-3 pt-3 border-t border-arena-200/40 dark:border-arena-700/40 grid grid-cols-2 gap-3">
          <div className="min-w-0">
            <div className="text-[15px] sm:text-[17px] font-display font-extrabold text-cyan-700 dark:text-cyan-300 tabular-nums leading-none">
              {inr(wallet)}
            </div>
            <div className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-0.5 leading-none">pool wallet</div>
          </div>
          <div className="min-w-0">
            <div className={`text-[15px] sm:text-[17px] font-display font-extrabold tabular-nums leading-none ${
              depTarget === 0 ? 'text-arena-700 dark:text-neon-200/80'
              : depShort      ? 'text-amber-700 dark:text-amber-300'
                              : 'text-emerald-700 dark:text-emerald-300'
            }`}>
              {depTarget > 0 ? `${depPct}%` : inr(depBal)}
            </div>
            <div className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-0.5 leading-none">
              deposit{depTarget > 0 ? ` · ${inr(depBal)} / ${inr(depTarget)}` : ''}
            </div>
          </div>
        </div>

        {/* Acceptance toggle chips — visible on every tab so flipping
            is one tap from anywhere */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <AcceptingChip
            kind="players" on={!!a.is_accepting_players}
            onToggle={(next) => onToggleAccepting('players', next)}
          />
          <AcceptingChip
            kind="agents" on={!!a.is_accepting_agents}
            onToggle={(next) => onToggleAccepting('agents', next)}
          />
        </div>
      </div>
    </motion.div>
  );
}

function AcceptingChip({
  kind, on, onToggle,
}: {
  kind: 'players' | 'agents';
  on: boolean;
  onToggle: (next: boolean) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const label = kind === 'players' ? 'players' : 'sub-agents';
  return (
    <button
      onClick={async () => { setBusy(true); try { await onToggle(!on); } finally { setBusy(false); } }}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider transition
        ring-1 ring-inset disabled:opacity-50 ${
          on
            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200/60 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30 hover:bg-emerald-100/80 dark:hover:bg-emerald-400/20'
            : 'bg-amber-50 text-amber-700 ring-amber-200/70 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30 hover:bg-amber-100/80 dark:hover:bg-amber-400/20'
        }`}>
      <Icon name={on ? 'task_alt' : 'pause_circle'} size={11} filled />
      {on ? `accepting ${label}` : `paused: ${label}`}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  OverviewTab — single-fetch dashboard
// ═══════════════════════════════════════════════════════════════════

function OverviewTab({
  agentId, agent, onAdjust, onOpenMoney, onOpenDownline,
}: {
  agentId: number;
  agent: Agent;
  onAdjust: () => void;
  onOpenMoney: () => void;
  onOpenDownline: () => void;
}) {
  const [dl, setDl]   = useState<DownlineAggregate | null>(null);
  const [sessActive, setSessActive] = useState<number | null>(null);
  const [auditCount, setAuditCount] = useState<number | null>(null);
  const [loading, setLoading]       = useState(true);
  const [err, setErr]               = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const [agg, sess, aud] = await Promise.all([
          adminApi.get<DownlineAggregate>(`/admin/agents/${agentId}/downline-aggregate`),
          adminApi.get<{ active: number }>(`/admin/agents/${agentId}/sessions?limit=1`),
          adminApi.get<{ total: number }>(`/admin/agents/${agentId}/audit?limit=1`),
        ]);
        if (cancelled) return;
        setDl(agg);
        setSessActive(Number(sess.active || 0));
        setAuditCount(Number(aud.total || 0));
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message || 'Failed to load overview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId]);

  const depTarget = Number(agent.security_deposit_target_points || 0);
  const depBal    = Number(agent.deposit_balance || 0);

  return (
    <div className="space-y-4">
      {/* Money tiles */}
      <div>
        <SectionTitle icon="payments" tone="emerald">Money</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <StatTile label="Pool wallet" value={agent.wallet_balance} tone="cyan"   loading={false} primary />
          <StatTile label="Deposit"     value={depBal}               tone={depBal >= depTarget && depTarget > 0 ? 'emerald' : 'amber'} loading={false} />
          <StatTile label="Deposit target" value={depTarget}         tone="violet" loading={false} />
          <StatTile label="Downline pool"  value={dl?.total_pool_balance ?? 0} tone="emerald" loading={loading} />
        </div>
      </div>

      {/* Downline tiles */}
      <div>
        <SectionTitle icon="family_history" tone="violet">Downline</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <CountTile label="Direct players"  value={agent.owned_players} icon="group" loading={false} />
          <CountTile label="Sub-team players" value={dl?.total_players ?? 0} icon="group_add" loading={loading} />
          <CountTile label="Sub-agents (deep)" value={dl?.total_descendants ?? 0} icon="account_tree" loading={loading} />
          <CountTile label="Max depth"       value={dl?.max_depth ?? 0} icon="layers" loading={loading} />
        </div>
      </div>

      {/* Activity tiles */}
      <div>
        <SectionTitle icon="bolt" tone="cyan">Activity</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <RelTile label="Last login"   value={agent.last_login_at} loading={false} />
          <CountTile label="Failed logins" value={agent.failed_login_attempts} icon="block" loading={false} />
          <CountTile label="Active sessions" value={sessActive ?? 0} icon="devices" loading={loading} />
          <CountTile label="Audit events"   value={auditCount ?? 0} icon="receipt_long" loading={loading} />
        </div>
      </div>

      {/* CTAs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Card>
          <div className="flex items-start gap-3 mb-3">
            <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0
                             bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200/60
                             dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
              <Icon name="tune" size={18} filled />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-neutral-900 dark:text-white leading-tight">
                Adjust pool wallet
              </div>
              <div className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mt-1 leading-snug">
                Direct credit / debit on this agent's pool. Audit + ledger trail preserved.
              </div>
            </div>
          </div>
          <button
            onClick={onAdjust}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full
                       bg-violet-600 hover:bg-violet-500 text-white text-[12.5px] font-bold
                       shadow-[0_2px_10px_rgba(139,92,246,0.45)] transition active:scale-95">
            <Icon name="account_balance_wallet" size={14} /> Open adjust modal
          </button>
        </Card>
        <Card>
          <div className="flex items-start gap-3 mb-3">
            <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0
                             bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-200/60
                             dark:bg-cyan-400/15 dark:text-cyan-300 dark:ring-cyan-400/30">
              <Icon name="family_history" size={18} filled />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-neutral-900 dark:text-white leading-tight">
                Inspect downline
              </div>
              <div className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mt-1 leading-snug">
                See every sub-agent under this one (depth 2), their pool, and player counts.
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onOpenDownline}
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full
                         bg-cyan-600 hover:bg-cyan-500 text-white text-[12.5px] font-bold
                         shadow-[0_2px_10px_rgba(6,182,212,0.4)] transition active:scale-95">
              <Icon name="account_tree" size={14} /> Open downline
            </button>
            <button
              onClick={onOpenMoney}
              className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-full
                         ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                         text-arena-700 dark:text-neon-200/85 hover:bg-arena-100/60 dark:hover:bg-arena-700/60
                         text-[12.5px] font-bold transition">
              <Icon name="account_balance_wallet" size={14} />
            </button>
          </div>
        </Card>
      </div>

      {err && <Banner tone="error">{err}</Banner>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  IdentityGeographyTab
// ═══════════════════════════════════════════════════════════════════

function IdentityGeoTab({
  sa, isAdmin, busy, setBusy, setErr, note, reload,
}: {
  sa: Agent; isAdmin: boolean; busy: boolean;
  setBusy: (b: boolean) => void;
  setErr: (s: string | null) => void;
  note: (msg: string) => void;
  reload: () => Promise<void>;
}) {
  return (
    <div className="space-y-3">
      {/* 2026-05-25 — admin can now fully edit an agent's profile, same
          as the player edit panel. Admin-only; agents edit their own via
          /admin/profile. */}
      {isAdmin && (
        <AgentProfileEditCard
          sa={sa}
          onSaved={async (msg) => { note(msg); await reload(); }}
          onError={(m) => setErr(m)}
        />
      )}

      <Card>
        <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
          <Icon name="badge" size={16} className="text-violet-600 dark:text-violet-300" />
          Account
        </h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          {sa.uaid && (
            <Info k="UAID" v={sa.uaid} />
          )}
          <Info k="Joined"     v={new Date(sa.created_at).toLocaleString()} />
          <Info k="Last login" v={sa.last_login_at ? new Date(sa.last_login_at).toLocaleString() : '—'} />
          {sa.last_login_ip && <Info k="Last login IP" v={sa.last_login_ip} />}
          <Info k="Level"      v={`${levelName(sa.level || 1)}`} />
          <Info k="Parent"     v={sa.parent_username ? `@${sa.parent_username}` : 'Admin-direct'} />
        </dl>

        {/* Identity-lock toggle */}
        {isAdmin && (
          <div className="mt-4 pt-3 border-t border-arena-200/40 dark:border-arena-700/40 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[12.5px] font-bold text-neutral-900 dark:text-white inline-flex items-center gap-1">
                <Icon name={sa.identity_locked ? 'lock' : 'lock_open'} size={14}
                  className={sa.identity_locked ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'} />
                {sa.identity_locked ? 'Identity locked' : 'Identity unlocked'}
              </div>
              <div className="text-[11px] text-arena-600 dark:text-neon-200/65 mt-0.5">
                {sa.identity_locked
                  ? 'Agent can\'t self-edit username / email / photo / password.'
                  : 'Agent can self-edit profile from /admin/profile.'}
              </div>
            </div>
            <button
              disabled={busy}
              onClick={async () => {
                setBusy(true); setErr(null);
                try {
                  await adminApi.post(`/admin/agents/${sa.id}/identity-lock`,
                    { locked: !sa.identity_locked });
                  note(sa.identity_locked ? 'Profile unlocked' : 'Profile locked');
                  await reload();
                } catch (e) { setErr((e as ApiError).message); }
                finally { setBusy(false); }
              }}
              className={`shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-full text-[12px] font-bold
                          ${sa.identity_locked
                            ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_2px_10px_rgba(16,185,129,0.4)]'
                            : 'bg-amber-600 hover:bg-amber-500 text-white shadow-[0_2px_10px_rgba(245,158,11,0.4)]'}
                          transition active:scale-95 disabled:opacity-50`}>
              <Icon name={sa.identity_locked ? 'lock_open' : 'lock'} size={13} />
              {sa.identity_locked ? 'Unlock' : 'Lock'}
            </button>
          </div>
        )}
      </Card>

      {/* Geography */}
      {isAdmin && (
        <Card>
          <AgentLocationEditor
            agent={sa}
            onSaved={async (msg) => { note(msg); await reload(); }}
            onError={(m) => setErr(m)}
          />
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  AgentProfileEditCard — admin edits an agent's full profile
//  (parity with the player edit panel: username/email/phone/name/DOB/
//  gender). PATCHes /admin/agents/:id; the backend updateSchema + the
//  AgentModel.update whitelist accept these fields. Admin-only.
// ═══════════════════════════════════════════════════════════════════

function AgentProfileEditCard({
  sa, onSaved, onError,
}: {
  sa: Agent;
  onSaved: (msg: string) => void | Promise<void>;
  onError: (m: string) => void;
}) {
  type Editable = {
    username: string; email: string; phone: string;
    first_name: string; last_name: string; birthdate: string;
    gender: '' | 'male' | 'female' | 'other' | 'prefer_not';
  };
  const seed = (a: Agent): Editable => ({
    username:   a.username || '',
    email:      a.email || '',
    phone:      a.phone || '',
    first_name: a.first_name || '',
    last_name:  a.last_name || '',
    // birthdate may arrive as an ISO datetime; the date input needs YYYY-MM-DD.
    birthdate:  a.birthdate ? String(a.birthdate).slice(0, 10) : '',
    gender:     (a.gender as Editable['gender']) || '',
  });
  const [form, setForm] = useState<Editable>(seed(sa));
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(seed(sa)); }, [sa]);

  const set = <K extends keyof Editable>(k: K, v: Editable[K]) => setForm((f) => ({ ...f, [k]: v }));

  const baseline = seed(sa);
  const dirty = (Object.keys(form) as (keyof Editable)[]).some((k) => form[k] !== baseline[k]);

  async function save() {
    setBusy(true); onError('');
    const patch: Record<string, unknown> = {};
    // Only forward changed fields. Identity strings: skip empty (the
    // backend requires min length); gender can be cleared to null.
    for (const k of ['username','email','phone','first_name','last_name','birthdate'] as const) {
      if (form[k] !== baseline[k] && form[k].trim() !== '') patch[k] = form[k].trim();
    }
    if (form.gender !== baseline.gender) patch.gender = form.gender || null;
    if (!Object.keys(patch).length) { setBusy(false); onSaved('Nothing to save'); return; }
    try {
      await adminApi.patch(`/admin/agents/${sa.id}`, patch);
      await onSaved(`Saved ${Object.keys(patch).length} field(s)`);
    } catch (e) {
      onError((e as ApiError).message);
    } finally { setBusy(false); }
  }

  const TextField = (label: string, field: keyof Editable, type = 'text') => (
    <label className="block">
      <span className="block text-[11px] font-bold uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1">{label}</span>
      <input type={type} value={form[field]}
             onChange={(e) => set(field, e.target.value as Editable[keyof Editable])}
             className={INPUT_CLS} />
    </label>
  );

  return (
    <Card>
      <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-4">
        <Icon name="edit" size={16} className="text-violet-600 dark:text-violet-300" />
        Edit profile
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {TextField('Username', 'username')}
        {TextField('Email', 'email', 'email')}
        {TextField('Phone', 'phone')}
        {TextField('First name', 'first_name')}
        {TextField('Last name', 'last_name')}
        {TextField('Date of birth', 'birthdate', 'date')}
        <label className="block">
          <span className="block text-[11px] font-bold uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1">Gender</span>
          <select value={form.gender} onChange={(e) => set('gender', e.target.value as Editable['gender'])}
                  className={INPUT_CLS}>
            <option value="">—</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not">Prefer not to say</option>
          </select>
        </label>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={save} disabled={busy || !dirty}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-bold
                           bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                           text-white shadow-[0_2px_10px_rgba(139,92,246,0.35)]
                           transition active:scale-95 disabled:opacity-50">
          {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" onClick={() => setForm(seed(sa))} disabled={busy || !dirty}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-bold
                           bg-arena-100/60 hover:bg-arena-200/70
                           dark:bg-arena-700/40 dark:hover:bg-arena-700/60
                           text-arena-700 dark:text-neon-200/75
                           ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                           transition active:scale-95 disabled:opacity-40">
          <Icon name="undo" size={13} /> Reset
        </button>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  MoneyTab — pool + deposit + payment config
// ═══════════════════════════════════════════════════════════════════

function MoneyTab({
  sa, isAdmin, paymentConfig, ledger, onAdjust, reload, note,
}: {
  sa: Agent; isAdmin: boolean;
  paymentConfig: PaymentConfig;
  ledger: { rows: LedgerRow[]; total: number; balance: number } | null;
  onAdjust: () => void;
  reload: () => Promise<void>;
  note: (s: string) => void;
}) {
  return (
    <div className="space-y-3">
      {/* Pool wallet card */}
      <Card>
        <div className="flex items-start gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0
                           bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-200/60
                           dark:bg-cyan-400/15 dark:text-cyan-300 dark:ring-cyan-400/30">
            <Icon name="account_balance_wallet" size={20} filled />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55">
              Pool balance
            </div>
            <div className="font-display font-extrabold text-[22px] sm:text-[28px] text-cyan-700 dark:text-cyan-300 tabular-nums leading-none mt-0.5">
              {inr(ledger?.balance ?? sa.wallet_balance)}
            </div>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={onAdjust}
            className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full
                       bg-violet-600 hover:bg-violet-500 text-white text-[12.5px] font-bold
                       shadow-[0_2px_10px_rgba(139,92,246,0.45)] transition active:scale-95">
            <Icon name="tune" size={14} /> Direct credit / debit
          </button>
        )}
      </Card>

      {/* Admin → agent topup, agent → admin withdraw */}
      {isAdmin && (
        <Card>
          <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
            <Icon name="swap_vert" size={16} className="text-violet-600 dark:text-violet-300" />
            Pool moves (admin ⇄ agent)
          </h3>
          <TopupForm agentId={sa.id} onTopped={() => { note('Topup recorded'); void reload(); }} />
          <WithdrawForm agentId={sa.id} onDone={() => { note('Withdrawal recorded'); void reload(); }} />
        </Card>
      )}

      {/* Wallet ledger */}
      <Card>
        <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
          <Icon name="receipt_long" size={16} className="text-emerald-600 dark:text-emerald-300" />
          Wallet ledger
        </h3>
        {!ledger || ledger.rows.length === 0
          ? <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">No movements yet.</div>
          : (
            <ul className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
              {ledger.rows.map((r) => {
                const meta = KIND_LABEL[r.kind] || { label: r.kind, tone: 'neutral' as const, icon: 'circle' };
                const positive = r.amount > 0;
                const negative = r.amount < 0;
                return (
                  <li key={r.id}
                      className="flex items-center gap-2.5 rounded-xl px-3 py-2.5
                                 bg-arena-50/40 dark:bg-arena-800/40
                                 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
                    <span className={`grid place-items-center w-7 h-7 rounded-full shrink-0 ${
                      positive ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                      : negative ? 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'
                      : 'bg-arena-100/60 text-arena-600 dark:bg-arena-700/50 dark:text-neon-200/70'
                    }`}>
                      <Icon name={meta.icon} size={14} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-semibold text-neutral-900 dark:text-white truncate">
                        {meta.label}
                      </div>
                      {r.note && (
                        <div className="text-[11px] text-arena-700 dark:text-neon-200/75 truncate">
                          {r.note}
                        </div>
                      )}
                      <div className="text-[10px] text-arena-600 dark:text-neon-200/55">
                        {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                        {r.actor_admin_id != null && <span> · by admin #{r.actor_admin_id}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-[13px] font-bold tabular-nums ${
                        positive ? 'text-emerald-700 dark:text-emerald-300'
                        : negative ? 'text-rose-700 dark:text-rose-300'
                        : 'text-arena-700 dark:text-neon-200/75'
                      }`}>
                        {positive ? '+' : ''}{inr(Math.abs(r.amount))}
                      </div>
                      {r.balance_after != null && (
                        <div className="text-[10px] text-arena-600 dark:text-neon-200/55 tabular-nums">
                          bal {inr(r.balance_after)}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        {ledger && ledger.total > ledger.rows.length && (
          <div className="text-[11px] text-arena-600 dark:text-neon-200/65 mt-2">
            Showing {ledger.rows.length} of {ledger.total}.
          </div>
        )}
      </Card>

      {/* Deposit ledger (DepositLedgerTab reads /agents/:id/deposit/ledger
          via useParams — it operates self-contained inside this Card) */}
      <Card>
        <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
          <Icon name="savings" size={16} className="text-emerald-600 dark:text-emerald-300" />
          Security deposit
        </h3>
        <DepositLedgerTab />
      </Card>

      {/* Payment config */}
      <Card>
        <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
          <Icon name="qr_code_2" size={16} className="text-cyan-600 dark:text-cyan-300" />
          Payment system
        </h3>
        {!paymentConfig || (
          !paymentConfig.upi_id && !paymentConfig.qr_image_url &&
          !paymentConfig.whatsapp_number && !paymentConfig.support_phone &&
          !paymentConfig.subagent_upi_id && !paymentConfig.subagent_qr_image_url &&
          !paymentConfig.subagent_whatsapp_number && !paymentConfig.subagent_support_phone
        ) ? (
          <div className="rounded-xl bg-amber-500/10 ring-1 ring-inset ring-amber-500/30 text-amber-700 dark:text-amber-200 px-3 py-2.5 text-[12px]">
            <Icon name="warning" size={14} className="inline -mt-0.5 mr-1" />
            This agent hasn't configured any payment method yet. Their players can't recharge until at least a UPI or WhatsApp is set.
          </div>
        ) : (
          <>
            {!paymentConfig.is_active && (
              <div className="mb-3 rounded-xl bg-amber-500/10 ring-1 ring-inset ring-amber-500/30 text-amber-700 dark:text-amber-200 px-3 py-2 text-[11.5px]">
                <Icon name="visibility_off" size={13} className="inline -mt-0.5 mr-1" />
                Inactive — players see admin's payment details instead.
              </div>
            )}
            {/* Both audiences, side by side: what this agent shows its
                PLAYERS vs what it shows its SUB-AGENTS. Sub-agent fields
                fall back to the player-facing ones when left blank
                (matches the backend per-field fallback), so the admin
                always sees the EFFECTIVE QR + handle each audience gets,
                with an "(from players)" tag on inherited values. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <PaymentAudienceCard cfg={paymentConfig} audience="players" />
              <PaymentAudienceCard cfg={paymentConfig} audience="agents" />
            </div>
            <div className="text-[10.5px] text-arena-500 dark:text-neon-200/55 mt-2.5">
              Last updated {new Date(paymentConfig.updated_at).toLocaleString()}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  PaymentAudienceCard — one audience's effective QR + handles
//
//  Admin oversight read-only view. Renders the QR image + UPI +
//  WhatsApp + support phone + note that THIS agent shows either its
//  players or its sub-agents. Sub-agent variants fall back to the
//  player-facing pair per-field when blank (mirrors the backend), so
//  we render the EFFECTIVE value and tag inherited ones "(from
//  players)". Players have no fallback.
// ═══════════════════════════════════════════════════════════════════

function PaymentAudienceCard({
  cfg, audience,
}: {
  cfg: NonNullable<PaymentConfig>;
  audience: 'players' | 'agents';
}) {
  const isAgents = audience === 'agents';

  // Values this agent explicitly set for THIS audience.
  const ownUpi   = isAgents ? cfg.subagent_upi_id          : cfg.upi_id;
  const ownQr    = isAgents ? cfg.subagent_qr_image_url    : cfg.qr_image_url;
  const ownWa    = isAgents ? cfg.subagent_whatsapp_number : cfg.whatsapp_number;
  const ownPhone = isAgents ? cfg.subagent_support_phone   : cfg.support_phone;
  const ownNote  = isAgents ? cfg.subagent_display_note    : cfg.display_note;

  // Effective values — sub-agents inherit the player-facing value when
  // their own is unset. Players have no fallback (the second operand
  // is null for the 'players' audience).
  const effUpi   = ownUpi   ?? (isAgents ? cfg.upi_id         : null);
  const effQr    = ownQr    ?? (isAgents ? cfg.qr_image_url    : null);
  const effWa    = ownWa    ?? (isAgents ? cfg.whatsapp_number : null);
  const effPhone = ownPhone ?? (isAgents ? cfg.support_phone   : null);

  // True when the effective value came from the player-facing pair
  // because this audience's own variant was blank.
  const inherited = (own: string | null, eff: string | null) => isAgents && !own && !!eff;

  const ring  = isAgents ? 'ring-emerald-200/60 dark:ring-emerald-400/25' : 'ring-sky-200/60 dark:ring-sky-400/25';
  const pill  = isAgents ? 'bg-emerald-600 text-white dark:bg-emerald-500' : 'bg-sky-600 text-white dark:bg-sky-500';
  const title = isAgents ? 'What sub-agents see' : 'What players see';
  const qrInherited = inherited(ownQr, effQr);

  return (
    <div className={`rounded-xl p-3 ring-1 ring-inset ${ring} bg-arena-50/40 dark:bg-arena-800/30`}>
      <div className="mb-2.5">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.1em] ${pill}`}>
          <Icon name={isAgents ? 'hub' : 'group'} size={10} filled />
          {title}
        </span>
      </div>

      <div className="flex items-start gap-3">
        {/* QR thumbnail (click to open full size) or placeholder */}
        <div className="shrink-0">
          {effQr ? (
            <a href={effQr} target="_blank" rel="noreferrer"
               className="block w-[108px] h-[108px] rounded-xl overflow-hidden bg-white p-1.5
                          ring-1 ring-inset ring-arena-200/70 dark:ring-arena-600/50
                          hover:ring-emerald-300 dark:hover:ring-emerald-400/40 transition">
              <img src={effQr} alt={`${title} QR`} className="w-full h-full object-contain" />
            </a>
          ) : (
            <div className="w-[108px] h-[108px] rounded-xl grid place-items-center
                            bg-arena-50/60 dark:bg-arena-900/40
                            border-2 border-dashed border-arena-300/60 dark:border-arena-600/60">
              <Icon name="qr_code_2" size={28} className="text-arena-400 dark:text-neon-200/35" />
            </div>
          )}
          <div className="text-[9.5px] text-center mt-1 max-w-[108px] leading-tight">
            {!effQr
              ? <span className="text-arena-500 dark:text-neon-200/45 italic">No QR uploaded</span>
              : qrInherited
                ? <span className="text-arena-500 dark:text-neon-200/55">QR from players</span>
                : <span className="text-arena-500 dark:text-neon-200/55">QR code</span>}
          </div>
        </div>

        {/* Handles */}
        <div className="min-w-0 flex-1 space-y-2">
          <KV label="UPI" value={effUpi} mono inherited={inherited(ownUpi, effUpi)} />
          <KV label="WhatsApp" value={effWa} mono inherited={inherited(ownWa, effWa)} />
          <KV label="Support phone" value={effPhone} mono inherited={inherited(ownPhone, effPhone)} />
          {ownNote && <KV label="Note" value={ownNote} />}
        </div>
      </div>
    </div>
  );
}

function KV({
  label, value, mono, inherited,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  inherited?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55">
        {label}
      </div>
      <div className={`text-[12.5px] text-neutral-900 dark:text-white break-all leading-tight ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-arena-400 dark:text-neon-200/40 italic font-sans">—</span>}
        {inherited && value && (
          <span className="ml-1 align-middle text-[9px] font-sans not-italic font-semibold
                           text-arena-500 dark:text-neon-200/50 whitespace-nowrap">
            (from players)
          </span>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  PartnershipPermissionsTab — composes existing matrices
// ═══════════════════════════════════════════════════════════════════

function PartnershipPermsTab({
  sa, isAdmin, schema, onPermissionsSaved, onLocksSaved, onPartnershipSaved, setTabDirty, setErr,
}: {
  sa: Agent; isAdmin: boolean; schema: PermSchema | null;
  onPermissionsSaved: () => void;
  onLocksSaved: () => void;
  onPartnershipSaved: () => void;
  setTabDirty: (d: boolean) => void;
  setErr: (s: string | null) => void;
}) {
  // Combine the matrices' dirty signals into one tab-level dirty flag.
  const [permDirty, setPermDirty] = useState(false);
  const [lockDirty, setLockDirty] = useState(false);
  useEffect(() => {
    setTabDirty(permDirty || lockDirty);
    return () => setTabDirty(false);
  }, [permDirty, lockDirty, setTabDirty]);

  if (!isAdmin) {
    return (
      <Card>
        <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">
          Partnership & permissions are admin-only.
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <PartnershipEditor agentId={sa.id} onSaved={onPartnershipSaved} />
        <div className="border-t border-arena-200/60 dark:border-white/10 pt-4 mt-4"><SportRatesEditor agentId={sa.id} onSaved={onPartnershipSaved} /></div>
      </Card>

      {schema && (
        <Card>
          <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
            <Icon name="vpn_key" size={16} className="text-emerald-600 dark:text-emerald-300" />
            Permissions
          </h3>
          <PermissionsMatrix
            sa={sa} schema={schema}
            onSaved={onPermissionsSaved}
            setErr={setErr}
            onDirtyChange={setPermDirty}
          />
        </Card>
      )}

      <Card>
        <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
          <Icon name="lock" size={16} className="text-amber-600 dark:text-amber-300" />
          Identity field locks
        </h3>
        <IdentityLockFieldsMatrix
          sa={sa}
          onSaved={onLocksSaved}
          setErr={setErr}
          onDirtyChange={setLockDirty}
        />
      </Card>

      <Card>
        <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
          <Icon name="history" size={16} className="text-cyan-600 dark:text-cyan-300" />
          Rate history
        </h3>
        <RateHistoryCard agentId={sa.id} />
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  DownlineTab — depth-2 tree + aggregate strip
// ═══════════════════════════════════════════════════════════════════

function DownlineTab({ agentId }: { agentId: number }) {
  const [agg, setAgg]       = useState<DownlineAggregate | null>(null);
  const [nodes, setNodes]   = useState<DownlineNode[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const [a, t] = await Promise.all([
          adminApi.get<DownlineAggregate>(`/admin/agents/${agentId}/downline-aggregate`),
          adminApi.get<{ rows: DownlineNode[]; depth: number }>(`/admin/agents/${agentId}/downline-tree?depth=2`),
        ]);
        if (cancelled) return;
        setAgg(a);
        setNodes(t.rows);
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [agentId]);

  // Group by depth for visual hierarchy.
  const direct   = (nodes || []).filter((n) => n.depth === 1);
  const indirect = (nodes || []).filter((n) => n.depth === 2);

  const matches = (n: DownlineNode) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return n.username.toLowerCase().includes(q)
        || (n.uaid || '').toLowerCase().includes(q)
        || n.email.toLowerCase().includes(q);
  };

  return (
    <div className="space-y-3">
      {/* Aggregate strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <CountTile label="Total descendants" value={agg?.total_descendants ?? 0} icon="account_tree" loading={loading} />
        <CountTile label="Active descendants" value={agg?.active_descendants ?? 0} icon="task_alt" loading={loading} />
        <CountTile label="Sub-team players" value={agg?.total_players ?? 0} icon="group" loading={loading} />
        <StatTile  label="Sub-team pool"    value={agg?.total_pool_balance ?? 0} tone="emerald" loading={loading} />
      </div>

      {/* Tree — depth 2 */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="flex-1 font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2">
            <Icon name="family_history" size={16} className="text-violet-600 dark:text-violet-300" />
            Tree (depth 2)
          </h3>
          {nodes && nodes.length > 0 && (
            <div className="relative w-44">
              <Icon name="search" size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-arena-500 dark:text-neon-200/60" />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="filter"
                className={INPUT_CLS + ' pl-7 py-1.5 text-[12px]'} />
            </div>
          )}
        </div>

        {loading && <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">Loading downline…</div>}
        {!loading && nodes && nodes.length === 0 && (
          <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">
            No sub-agents under this one yet.
          </div>
        )}

        {!loading && direct.length > 0 && (
          <div className="space-y-2">
            <SectionTitle icon="arrow_downward" tone="cyan">Direct ({direct.length})</SectionTitle>
            <div className="space-y-1.5">
              {direct.filter(matches).map((n) => (
                <DownlineNodeCard key={n.id} node={n} indent={0} />
              ))}
            </div>
          </div>
        )}

        {!loading && indirect.length > 0 && (
          <div className="space-y-2 mt-4">
            <SectionTitle icon="subdirectory_arrow_right" tone="violet">
              In their sub-teams ({indirect.length})
            </SectionTitle>
            <div className="space-y-1.5">
              {indirect.filter(matches).map((n) => (
                <DownlineNodeCard key={n.id} node={n} indent={1} />
              ))}
            </div>
          </div>
        )}

        {agg && agg.max_depth > 2 && (
          <div className="mt-3 text-[11px] text-arena-600 dark:text-neon-200/65 inline-flex items-center gap-1">
            <Icon name="info" size={11} />
            This tree extends to depth {agg.max_depth}; this view shows the first 2 levels. Drill into any direct sub-agent to keep going.
          </div>
        )}
      </Card>

      {err && <Banner tone="error">{err}</Banner>}
    </div>
  );
}

function DownlineNodeCard({ node: n, indent }: { node: DownlineNode; indent: 0 | 1 }) {
  const inactive = n.status !== 'active';
  return (
    <Link
      to={`/people/agents/${n.id}`}
      className={`block rounded-xl ring-1 ring-inset transition
                  ${indent ? 'ml-3 sm:ml-5' : ''}
                  bg-arena-50/60 hover:bg-arena-100/60 ring-arena-200/60
                  dark:bg-arena-700/35 dark:hover:bg-arena-700/60 dark:ring-arena-600/40
                  hover:ring-emerald-300 dark:hover:ring-emerald-400/40`}>
      <div className="flex items-center gap-2.5 p-2.5">
        {indent === 1 && (
          <Icon name="subdirectory_arrow_right" size={14} className="text-arena-400 dark:text-neon-200/45 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[12.5px] font-semibold text-neutral-900 dark:text-white truncate">
              @{n.username}
            </span>
            <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold leading-none
                             bg-violet-100 text-violet-700
                             dark:bg-violet-500/15 dark:text-violet-300">
              {levelName(n.level)}
            </span>
            {inactive && (
              <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold leading-none
                               bg-rose-100 text-rose-700
                               dark:bg-rose-500/15 dark:text-rose-300">
                {n.status}
              </span>
            )}
            {n.is_test ? (
              <Icon name="science" size={10} className="text-amber-600 dark:text-amber-300 shrink-0" />
            ) : null}
          </div>
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 truncate">
            {n.uaid ? `${n.uaid} · ` : ''}{n.email}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[12px] font-bold text-emerald-700 dark:text-emerald-300 tabular-nums leading-none">
            {inr(n.pool_balance)}
          </div>
          <div className="text-[10px] text-arena-600 dark:text-neon-200/60 mt-0.5 leading-none">
            {n.active_player_count} players · {n.child_agent_count} subs
          </div>
        </div>
      </div>
    </Link>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  OpsAuditTab — sessions, audit log, owned players, danger
// ═══════════════════════════════════════════════════════════════════

function OpsAuditTab({
  sa, isAdmin, players, onAssignPlayers, mutate, onDelete, reload, note, setErr,
}: {
  sa: Agent; isAdmin: boolean;
  players: { rows: PlayerRow[]; total: number } | null;
  onAssignPlayers: () => void;
  mutate: (path: string, body: unknown, message: string) => Promise<void>;
  onDelete: () => Promise<void>;
  reload: () => Promise<void>;
  note: (m: string) => void;
  setErr: (s: string | null) => void;
}) {
  return (
    <div className="space-y-3">
      <SessionsCard agentId={sa.id} />
      <AuditCard    agentId={sa.id} />

      {/* Owned players (carried over from legacy tab) */}
      <Card>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h3 className="flex-1 font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2">
            <Icon name="group" size={16} className="text-emerald-600 dark:text-emerald-300" />
            Owned players
            {players && <span className="text-[11px] font-normal text-arena-500 dark:text-neon-200/60">· {players.total}</span>}
          </h3>
          {isAdmin && (
            <button onClick={onAssignPlayers}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-full
                         bg-emerald-600 hover:bg-emerald-500 text-white text-[11.5px] font-bold
                         shadow-[0_2px_10px_rgba(16,185,129,0.35)] transition active:scale-95">
              <Icon name="group_add" size={13} /> Assign existing
            </button>
          )}
          <Link to={`/people/agents/${sa.id}/players/new`}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-full
                       bg-violet-600 hover:bg-violet-500 text-white text-[11.5px] font-bold
                       shadow-[0_2px_10px_rgba(139,92,246,0.4)] transition active:scale-95">
            <Icon name="person_add" size={13} /> Onboard
          </Link>
        </div>
        {!players || players.rows.length === 0
          ? <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">No players yet.</div>
          : (
            <ul className="space-y-1.5">
              {players.rows.map((p) => (
                <li key={p.id}
                    className="flex items-center gap-2.5 rounded-xl px-3 py-2
                               bg-arena-50/50 dark:bg-arena-800/40
                               ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-neutral-900 dark:text-white truncate">
                      {(((p.first_name || "") + " " + (p.last_name || "")).trim() || "—")}
                    </div>
                    <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 truncate">
                      {p.phone || p.email}
                    </div>
                  </div>
                  <div className="text-right shrink-0 mr-1">
                    <div className="text-[12px] font-bold text-emerald-700 dark:text-emerald-300 tabular-nums leading-none">
                      {inr(p.wallet_balance)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Link to={`/people/players/${p.id}`}
                      className="grid place-items-center w-7 h-7 rounded-full
                                 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                                 text-arena-600 dark:text-neon-200/70 hover:bg-arena-100/60 dark:hover:bg-arena-700/60"
                      title="Open player">
                      <Icon name="open_in_new" size={13} />
                    </Link>
                    {isAdmin && (
                      <button
                        onClick={async () => {
                          if (!confirm(`Detach @${p.username} from @${sa.username}?\nPlayer becomes platform-direct.`)) return;
                          try {
                            await adminApi.post(`/admin/users/${p.id}/owner`, { agent_id: null });
                            note(`Detached @${p.username}`);
                            await reload();
                          } catch (e) { setErr((e as ApiError).message); }
                        }}
                        className="grid place-items-center w-7 h-7 rounded-full
                                   bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-300
                                   ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30
                                   hover:bg-rose-100 dark:hover:bg-rose-500/25"
                        title="Detach from this agent">
                        <Icon name="link_off" size={13} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        {players && players.total > players.rows.length && (
          <div className="text-[11px] text-arena-600 dark:text-neon-200/65 mt-2">
            Showing {players.rows.length} of {players.total}.
          </div>
        )}
      </Card>

      {/* Danger zone */}
      {isAdmin && (
        <Card>
          <h3 className="font-bold text-[14px] text-rose-700 dark:text-rose-300 inline-flex items-center gap-2 mb-2">
            <Icon name="warning" size={16} filled />
            Danger zone
          </h3>
          <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mb-3 leading-relaxed">
            Hard-delete this agent. Their wallet ledger is removed permanently. Owned players become platform users.
            Reassign first if you only want to reorganise the org chart.
          </p>
          <button
            onClick={() => {
              if (!confirm(`Permanently delete @${sa.username}?\nThis cannot be undone.`)) return;
              void onDelete();
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-bold
                       bg-rose-600 hover:bg-rose-500 text-white shadow-[0_2px_10px_rgba(244,63,94,0.4)]
                       transition active:scale-95">
            <Icon name="delete_forever" size={14} /> Delete agent
          </button>
          {/* `mutate` is reserved for action-sheet wiring; OpsAuditTab
              triggers no other admin verbs directly. */}
          <span className="hidden" data-unused={typeof mutate} />
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Sessions / Audit / Rate-history sub-cards
// ═══════════════════════════════════════════════════════════════════

function SessionsCard({ agentId }: { agentId: number }) {
  const [data, setData] = useState<{ rows: SessionRow[]; total: number; active: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await adminApi.get<{ rows: SessionRow[]; total: number; active: number }>(
          `/admin/agents/${agentId}/sessions?limit=15`);
        if (!cancelled) setData(r);
      } catch (e) { if (!cancelled) setErr((e as ApiError).message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [agentId]);
  return (
    <Card>
      <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
        <Icon name="devices" size={16} className="text-cyan-600 dark:text-cyan-300" />
        Sessions
        {data && <span className="text-[11px] font-normal text-arena-500 dark:text-neon-200/60">· {data.active} active / {data.total} total</span>}
      </h3>
      {loading && <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">Loading…</div>}
      {!loading && data && data.rows.length === 0 && (
        <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">No refresh tokens issued yet.</div>
      )}
      {!loading && data && data.rows.length > 0 && (
        <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {data.rows.map((r) => {
            const active = !!r.is_active;
            return (
              <li key={r.id}
                  className="flex items-center gap-2.5 rounded-xl px-3 py-2
                             bg-arena-50/50 dark:bg-arena-800/40
                             ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
                <span className={`grid place-items-center w-7 h-7 rounded-full shrink-0 ${
                  active
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                    : 'bg-arena-100/60 text-arena-500 dark:bg-arena-700/50 dark:text-neon-200/55'
                }`}>
                  <Icon name={active ? 'circle' : 'circle'} size={10} filled={active} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold text-neutral-900 dark:text-white truncate">
                    {r.ip || 'unknown ip'}
                  </div>
                  <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 truncate">
                    {r.user_agent || 'unknown agent'}
                  </div>
                  <div className="text-[10px] text-arena-500 dark:text-neon-200/55">
                    issued {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    {r.revoked_at && <> · revoked {new Date(r.revoked_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</>}
                  </div>
                </div>
                <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold uppercase ${
                  active
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                    : 'bg-arena-100 text-arena-600 dark:bg-arena-700 dark:text-neon-200/60'
                }`}>
                  {active ? 'active' : 'expired'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {err && <Banner tone="error">{err}</Banner>}
    </Card>
  );
}

function AuditCard({ agentId }: { agentId: number }) {
  const [data, setData] = useState<{ rows: AuditEntry[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await adminApi.get<{ rows: AuditEntry[]; total: number }>(
          `/admin/agents/${agentId}/audit?limit=30`);
        if (!cancelled) setData(r);
      } catch (e) { if (!cancelled) setErr((e as ApiError).message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [agentId]);
  return (
    <Card>
      <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
        <Icon name="receipt_long" size={16} className="text-emerald-600 dark:text-emerald-300" />
        Audit log
        {data && <span className="text-[11px] font-normal text-arena-500 dark:text-neon-200/60">· {data.total}</span>}
      </h3>
      {loading && <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">Loading…</div>}
      {!loading && data && data.rows.length === 0 && (
        <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">No audit events for this agent yet.</div>
      )}
      {!loading && data && data.rows.length > 0 && (
        <ul className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
          {data.rows.map((r) => (
            <li key={r.id}
                className="rounded-xl px-3 py-2
                           bg-arena-50/50 dark:bg-arena-800/40
                           ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[12px] font-semibold text-neutral-900 dark:text-white">{r.event}</span>
                {r.actor_role && r.actor_username && (
                  <span className="text-[10px] text-arena-600 dark:text-neon-200/65">
                    by {r.actor_role} @{r.actor_username}
                  </span>
                )}
              </div>
              <div className="text-[10.5px] text-arena-500 dark:text-neon-200/55 mt-0.5">
                {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                {r.ip && <> · {r.ip}</>}
              </div>
              {r.meta && typeof r.meta === 'object' && Object.keys(r.meta as object).length > 0 && (
                <details className="mt-1">
                  <summary className="text-[10.5px] text-arena-600 dark:text-neon-200/65 cursor-pointer">meta</summary>
                  <pre className="mt-1 text-[10px] text-arena-600 dark:text-neon-200/65 whitespace-pre-wrap break-all">
                    {JSON.stringify(r.meta, null, 2)}
                  </pre>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
      {err && <Banner tone="error">{err}</Banner>}
    </Card>
  );
}

function RateHistoryCard({ agentId }: { agentId: number }) {
  const [data, setData] = useState<{ rows: RateHistoryRow[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await adminApi.get<{ rows: RateHistoryRow[]; total: number }>(
          `/admin/agents/${agentId}/rate-history?limit=30`);
        if (!cancelled) setData(r);
      } catch (e) { if (!cancelled) setErr((e as ApiError).message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [agentId]);
  if (loading) return <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">Loading…</div>;
  if (err) return <Banner tone="error">{err}</Banner>;
  if (!data || data.rows.length === 0) {
    return <div className="text-[12.5px] text-arena-600 dark:text-neon-200/65">No rate changes recorded yet.</div>;
  }
  return (
    <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
      {data.rows.map((r) => {
        const delta = Number(r.new_rate_pct) - Number(r.old_rate_pct);
        const up = delta > 0;
        return (
          <li key={r.id}
              className="flex items-center gap-2.5 rounded-xl px-3 py-2
                         bg-arena-50/50 dark:bg-arena-800/40
                         ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
            <span className={`grid place-items-center w-7 h-7 rounded-full shrink-0 ${
              up ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                 : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'
            }`}>
              <Icon name={up ? 'trending_up' : 'trending_down'} size={14} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-neutral-900 dark:text-white">
                {r.pillar}{r.game_id ? ` · ${r.game_id}` : ''}
              </div>
              <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 truncate">
                {r.reason || '—'}
              </div>
              <div className="text-[10px] text-arena-500 dark:text-neon-200/55">
                {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                {r.is_cascade ? ' · cascade' : ''}
              </div>
            </div>
            <div className="text-right shrink-0 text-[12px] tabular-nums">
              <div className="text-arena-600 dark:text-neon-200/65">{Number(r.old_rate_pct).toFixed(2)}%</div>
              <div className={`font-bold ${up ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                → {Number(r.new_rate_pct).toFixed(2)}%
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  AgentActionSheet — god-mode kebab menu
// ═══════════════════════════════════════════════════════════════════

function AgentActionSheet({
  a, isAdmin, onClose, onAdjust, onReassign, onResetPassword, onMutate, onDelete,
}: {
  a: Agent;
  isAdmin: boolean;
  onClose: () => void;
  onAdjust: () => void;
  onReassign: () => void;
  onResetPassword: () => void;
  onMutate: (path: string, body: unknown, message: string) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const isActive    = a.status === 'active';
  const isLockedOut = !!a.locked_until && new Date(a.locked_until) > new Date();
  const isTest      = !!a.is_test;

  const groups: ActionGroup[] = [
    {
      title: 'Wallet & ownership',
      items: [
        { key: 'adjust', icon: 'account_balance_wallet', label: 'Direct credit / debit pool',
          hint: `Current pool ${inr(a.wallet_balance || 0)}`,
          onSelect: () => onAdjust() },
        { key: 'reassign', icon: 'swap_horiz', label: 'Reassign parent',
          hint: a.parent_username ? `Currently under @${a.parent_username}` : 'Admin-direct',
          onSelect: () => onReassign() },
      ],
    },
    {
      title: 'Status',
      items: [
        { key: 'activate', icon: 'play_arrow', label: 'Reactivate', hidden: isActive,
          onSelect: () => onMutate(`/admin/agents/${a.id}`, { status: 'active' }, 'Reactivated') },
        { key: 'suspend',  icon: 'block', label: 'Suspend', tone: 'danger' as const, hidden: !isActive,
          confirm: { title: `Suspend @${a.username}?`, body: 'Agent signed out + blocked from logging in until reactivated.', cta: 'Suspend', ctaTone: 'danger' as const },
          onSelect: () => onMutate(`/admin/agents/${a.id}`, { status: 'suspended' }, 'Suspended') },
      ],
    },
    {
      title: 'Acceptance flags',
      items: [
        { key: 'toggle_players', icon: a.is_accepting_players ? 'pause_circle' : 'task_alt',
          label: a.is_accepting_players ? 'Pause accepting players' : 'Resume accepting players',
          hint: a.is_accepting_players ? 'Hides from player locator' : 'Re-enables player matching',
          onSelect: () => onMutate(`/admin/agents/${a.id}`,
            { is_accepting_players: !a.is_accepting_players },
            a.is_accepting_players ? 'Paused player intake' : 'Now accepting players') },
        { key: 'toggle_agents', icon: a.is_accepting_agents ? 'pause_circle' : 'task_alt',
          label: a.is_accepting_agents ? 'Pause accepting sub-agents' : 'Resume accepting sub-agents',
          hint: a.is_accepting_agents ? 'Locks creation of new sub-agents' : 'Allows creating new sub-agents',
          onSelect: () => onMutate(`/admin/agents/${a.id}`,
            { is_accepting_agents: !a.is_accepting_agents },
            a.is_accepting_agents ? 'Paused sub-agent intake' : 'Now accepting sub-agents') },
      ],
    },
    {
      title: 'Sessions & security',
      items: [
        { key: 'clear_lockout', icon: 'lock_open', label: 'Clear lockout',
          hint: isLockedOut ? `Locked until ${new Date(a.locked_until!).toLocaleTimeString('en-IN')}` : 'No active lockout',
          disabled: !isLockedOut && a.failed_login_attempts === 0,
          onSelect: () => onMutate(`/admin/agents/${a.id}/clear-lockout`, undefined, 'Lockout cleared') },
        { key: 'force_logout', icon: 'logout', label: 'Force logout', tone: 'danger' as const,
          hint: 'Revokes every refresh token',
          confirm: { title: `Force log @${a.username} out everywhere?`, body: 'Sessions terminated; password unchanged.', cta: 'Force logout', ctaTone: 'danger' as const },
          onSelect: () => onMutate(`/admin/agents/${a.id}/force-logout`, undefined, 'Logged out everywhere') },
        { key: 'reset_password', icon: 'password', label: 'Reset password',
          hint: 'Set a new password; agent is signed out everywhere',
          onSelect: () => { onClose(); onResetPassword(); } },
        { key: 'identity_lock', icon: a.identity_locked ? 'lock_open' : 'lock',
          label: a.identity_locked ? 'Unlock identity edits' : 'Lock identity edits',
          hint: a.identity_locked
            ? 'Agent can self-edit username / email / photo / password again'
            : 'Stops agent self-editing username / email / photo / password',
          onSelect: () => onMutate(`/admin/agents/${a.id}/identity-lock`,
            { locked: !a.identity_locked },
            a.identity_locked ? 'Identity unlocked' : 'Identity locked') },
      ],
    },
    ...(isAdmin ? [{
      title: 'Admin only',
      items: [
        { key: 'test', icon: isTest ? 'science_off' : 'science',
          label: isTest ? 'Unmark test account' : 'Mark as test account',
          hint: isTest ? 'Will count in analytics again' : 'Will be hidden from analytics',
          onSelect: () => onMutate(`/admin/agents/${a.id}/test-flag`,
            { is_test: !isTest }, isTest ? 'Unmarked test' : 'Marked test') },
        { key: 'delete', icon: 'delete_forever', label: 'Delete agent', tone: 'danger' as const,
          hint: 'Cascades wallet + tokens. Owned players become platform-direct.',
          confirm: { title: `Permanently delete @${a.username}?`, body: 'Cannot be undone. Their owned players are reassigned to platform-direct.', cta: 'Delete', ctaTone: 'danger' as const },
          onSelect: () => onDelete() },
      ],
    } satisfies ActionGroup] : []),
  ];

  return (
    <EntityActionSheet
      open
      onClose={onClose}
      title={`@${a.username}`}
      subtitle={`Agent #${a.id} · ${levelName(a.level || 1)}`}
      groups={groups}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Shared visual primitives — matches AdminUserDetail vocabulary
// ═══════════════════════════════════════════════════════════════════

function Card({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl p-4 sm:p-5
                 bg-white dark:bg-arena-700/40
                 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      {children}
    </motion.div>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success' | 'warn' | 'info'; children: React.ReactNode }) {
  const M = {
    error:   'bg-rose-50 ring-rose-200 text-rose-800 dark:bg-rose-500/10 dark:ring-rose-400/30 dark:text-rose-200',
    success: 'bg-emerald-50 ring-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:ring-emerald-400/30 dark:text-emerald-200',
    warn:    'bg-amber-50 ring-amber-200 text-amber-800 dark:bg-amber-500/10 dark:ring-amber-400/30 dark:text-amber-200',
    info:    'bg-arena-50/60 ring-arena-200/60 text-arena-700 dark:bg-arena-800/40 dark:ring-arena-600/40 dark:text-neon-200/75',
  } as const;
  return <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px] ${M[tone]}`}>{children}</div>;
}

function Info({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-arena-200/40 dark:border-arena-700/40 last:border-b-0">
      <dt className="text-[11px] text-arena-600 dark:text-neon-200/70 uppercase tracking-wider font-semibold">{k}</dt>
      <dd className={`text-[12.5px] text-neutral-900 dark:text-white text-right truncate ${mono ? 'font-mono tabular-nums tracking-[.08em]' : ''}`}>
        {v}
      </dd>
    </div>
  );
}

function SectionTitle({ icon, tone, children }: {
  icon: string; tone: 'emerald' | 'cyan' | 'violet'; children: React.ReactNode;
}) {
  const tones = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    cyan:    'text-cyan-700 dark:text-cyan-300',
    violet:  'text-violet-700 dark:text-violet-300',
  } as const;
  return (
    <h3 className={`text-[11px] font-bold uppercase tracking-wider mb-2 inline-flex items-center gap-1.5 ${tones[tone]}`}>
      <Icon name={icon} size={13} filled />
      {children}
    </h3>
  );
}

function StatTile({
  label, value, tone, loading, primary = false,
}: {
  label: string; value: number; tone: 'cyan' | 'emerald' | 'violet' | 'amber'; loading: boolean; primary?: boolean;
}) {
  const toneCls = {
    cyan:    'text-cyan-700 dark:text-cyan-300',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    violet:  'text-violet-700 dark:text-violet-300',
    amber:   'text-amber-700 dark:text-amber-300',
  }[tone];
  return (
    <div className="rounded-2xl p-3 bg-white dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40">
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55 leading-none">
        {label}
      </div>
      <div className={`mt-1.5 font-display font-extrabold tabular-nums leading-none ${toneCls} ${primary ? 'text-[20px] sm:text-[24px]' : 'text-[16px] sm:text-[20px]'}`}>
        {loading ? '—' : inr(value)}
      </div>
    </div>
  );
}

function CountTile({ label, value, icon, loading }: { label: string; value: number; icon: string; loading: boolean }) {
  return (
    <div className="rounded-2xl p-3 bg-white dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40">
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55 leading-none inline-flex items-center gap-1">
        <Icon name={icon} size={10} /> {label}
      </div>
      <div className="mt-1.5 text-[16px] sm:text-[20px] font-display font-extrabold text-neutral-900 dark:text-white tabular-nums leading-none">
        {loading ? '—' : value.toLocaleString('en-IN')}
      </div>
    </div>
  );
}

function RelTile({ label, value, loading }: { label: string; value: string | null | undefined; loading: boolean }) {
  const display = !value ? '—'
    : new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <div className="rounded-2xl p-3 bg-white dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40">
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55 leading-none">
        {label}
      </div>
      <div className="mt-1.5 text-[12px] font-semibold text-neutral-900 dark:text-white leading-tight truncate">
        {loading ? '—' : display}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Preserved legacy helpers — TopupForm, WithdrawForm, PermissionsMatrix,
//  IdentityLockFieldsMatrix, AgentLocationEditor
//
//  These are unchanged from the pre-rebuild version. Each is its own
//  self-contained matrix / form. The visual chrome they ship still
//  uses the older Section/Row vocabulary inside their own cards;
//  that's fine since they're slotted INSIDE the new <Card> wrappers
//  defined above, so the outer page chrome stays consistent.
// ═══════════════════════════════════════════════════════════════════

function TopupForm({ agentId, onTopped }: { agentId: number; onTopped: () => void }) {
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit() {
    if (amount <= 0) return;
    setBusy(true); setErr(null);
    try {
      await adminApi.post(`/admin/agents/${agentId}/wallet/topup`, { amount, note: note || null });
      setAmount(0); setNote('');
      onTopped();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }
  return (
    <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-3 space-y-2">
      <h4 className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1">
        <Icon name="add_circle" size={12} /> Topup pool (admin → agent)
      </h4>
      <div className="flex flex-wrap gap-2 items-center">
        <input type="number" min={1} value={amount || ''} onChange={(e) => setAmount(Number(e.target.value) || 0)}
          placeholder="Amount ₹" className={INPUT_CLS + ' w-32'} />
        <input value={note} onChange={(e) => setNote(e.target.value.slice(0, 190))}
          placeholder="Note (optional)" className={INPUT_CLS + ' flex-1 min-w-[140px]'} />
        <button onClick={submit} disabled={busy || amount <= 0}
          className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-1">
          {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="check" size={14} />}
          Topup
        </button>
      </div>
      {err && <div className="text-xs text-rose-700 dark:text-rose-300">{err}</div>}
    </div>
  );
}

function WithdrawForm({ agentId, onDone }: { agentId: number; onDone: () => void }) {
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit() {
    if (amount <= 0) return;
    if (!confirm(`Withdraw ₹${amount.toLocaleString('en-IN')} from this agent's pool?`)) return;
    setBusy(true); setErr(null);
    try {
      await adminApi.post(`/admin/agents/${agentId}/wallet/withdraw`, { amount, note: note || null });
      setAmount(0); setNote('');
      onDone();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }
  return (
    <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-3 space-y-2 mt-2">
      <h4 className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1">
        <Icon name="remove_circle" size={12} /> Withdraw from pool (agent → admin)
      </h4>
      <div className="flex flex-wrap gap-2 items-center">
        <input type="number" min={1} value={amount || ''} onChange={(e) => setAmount(Number(e.target.value) || 0)}
          placeholder="Amount ₹" className={INPUT_CLS + ' w-32'} />
        <input value={note} onChange={(e) => setNote(e.target.value.slice(0, 190))}
          placeholder="Note (e.g. cash settled)" className={INPUT_CLS + ' flex-1 min-w-[140px]'} />
        <button onClick={submit} disabled={busy || amount <= 0}
          className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-1">
          {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="check" size={14} />}
          Withdraw
        </button>
      </div>
      <p className="text-[10.5px] text-arena-600 dark:text-neon-200/60 leading-relaxed">
        Records that the agent paid you back cash. Cannot exceed the agent's current balance.
      </p>
      {err && <div className="text-xs text-rose-700 dark:text-rose-300">{err}</div>}
    </div>
  );
}

const PERMISSION_LABELS: Record<string, { label: string; hint: string }> = {
  users:             { label: 'Players (view/edit)',       hint: 'See and edit owned player profiles' },
  users_create:      { label: 'Create players',            hint: 'Onboard new player accounts (auto-owned)' },
  users_status:      { label: 'Suspend/ban players',       hint: "Change a player's status" },
  wallet_adjust:     { label: 'Adjust player wallet',      hint: 'Direct credit/debit (admin-style)' },
  wallet_topup:      { label: 'Recharge a player',         hint: 'Send money from own pool to a player' },
  payments_recharge: { label: 'Recharge requests',         hint: 'Approve/reject recharge submissions' },
  payments_withdraw: { label: 'Withdrawal requests',       hint: 'Mark paid / reject + refund withdrawals' },
  payment_settings:  { label: 'My UPI / QR config',        hint: 'Edit own payment receiver settings' },
  rewards_offers:    { label: 'Reward claim feed',         hint: 'See offer credits to owned players' },
  rewards_create:    { label: 'Create offers',             hint: 'Create offers funded from own pool' },
  earnings:          { label: 'My earnings ledger',        hint: 'See own commission/revenue history' },
  bulk_payouts:      { label: 'Bulk payouts',              hint: 'Run bulk credits to many players' },
  create_subagent:   { label: 'Create sub-agents',         hint: 'Onboard agents under self (carve-out)' },
};

function PermissionsMatrix({ sa, schema, onSaved, setErr, onDirtyChange }: {
  sa: Agent; schema: PermSchema; onSaved: () => void; setErr: (s: string | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [perm, setPerm]       = useState<Record<string, 'none'|'view'|'edit'>>(sa.permissions || {});
  const [initial, setInitial] = useState<Record<string, 'none'|'view'|'edit'>>(sa.permissions || {});
  const [busy, setBusy]       = useState(false);
  useEffect(() => {
    setPerm(sa.permissions || {});
    setInitial(sa.permissions || {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sa.id]);
  const isRowChanged = (k: string) => (perm[k] || 'none') !== (initial[k] || 'none');
  const dirty = schema.keys.some(isRowChanged);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);
  async function save() {
    setBusy(true); setErr(null);
    try {
      await adminApi.patch(`/admin/agents/${sa.id}`, { permissions: perm });
      setInitial(perm);
      onSaved();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }
  function discard() { setPerm(initial); }
  return (
    <div className="space-y-2">
      <p className="text-xs text-arena-600 dark:text-neon-200/60 mb-1">
        Each capability gets a level. <b>none</b> hides the page. <b>view</b> renders read-only. <b>edit</b> grants read+write.
      </p>
      <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 overflow-hidden">
        {schema.keys.map((k) => {
          const levels = schema.levels[k] || ['none','edit'];
          const cur = perm[k] || 'none';
          const meta = PERMISSION_LABELS[k] || { label: k, hint: '' };
          const changed = isRowChanged(k);
          return (
            <div key={k} className={`border-t ring-arena-200/60 dark:ring-arena-600/40 first:border-t-0 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 transition ${
              changed ? 'bg-amber-50/70 dark:bg-amber-500/[0.06] ring-1 ring-amber-300/60 dark:ring-amber-400/30 -mx-px' : ''
            }`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-arena-800 dark:text-neon-200/90 inline-flex items-center gap-2 flex-wrap">
                  {meta.label}
                  {changed && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 dark:bg-amber-400/20 dark:text-amber-200">
                      Modified · was {initial[k] || 'none'}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-arena-600 dark:text-neon-200/60">{meta.hint}</div>
              </div>
              <div className="flex gap-1 self-stretch sm:self-auto">
                {(['none','view','edit'] as const).map((lvl) => {
                  if (!levels.includes(lvl)) return null;
                  const active = cur === lvl;
                  return (
                    <button key={lvl}
                      onClick={() => setPerm({ ...perm, [k]: lvl })}
                      className={`flex-1 sm:flex-initial px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border transition ${
                        active
                          ? (lvl === 'edit' ? 'bg-emerald-600 border-emerald-700 text-white'
                            : lvl === 'view' ? 'bg-cyan-600 border-cyan-700 text-white'
                            : 'bg-violet-600 ring-1 ring-inset ring-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]')
                          : 'bg-white dark:bg-arena-800 ring-arena-200/60 dark:ring-arena-600/40 text-arena-400 dark:text-neon-200/70 hover:bg-arena-100/60 dark:hover:bg-arena-700/60'
                      }`}>
                      {lvl}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className={`sticky bottom-0 z-10 -mx-2 px-2 pt-2 pb-2 backdrop-blur-md transition ${
        dirty
          ? 'bg-amber-50/90 dark:bg-amber-500/[0.08] border-t border-amber-300/70 dark:border-amber-400/30'
          : 'bg-white/85 dark:bg-arena-900/85 border-t border-arena-200/40 dark:border-arena-700/40 backdrop-blur'
      }`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {dirty ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider
                             bg-amber-200 text-amber-900 dark:bg-amber-400/20 dark:text-amber-200
                             shadow-[0_0_12px_rgba(245,158,11,0.45)] dark:shadow-[0_0_14px_rgba(245,158,11,0.35)]">
              <Icon name="warning" size={12} filled /> Unsaved changes
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold
                             bg-arena-100/60 dark:bg-arena-800 text-arena-600 dark:text-neon-200/55">
              <Icon name="check_circle" size={12} /> All saved
            </span>
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={discard} disabled={!dirty || busy}
              className="px-3 py-2 rounded-xl text-[12px] font-semibold ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 text-arena-700 dark:text-neon-200/85 hover:bg-arena-50 dark:hover:bg-arena-700/60 disabled:opacity-40">
              Discard
            </button>
            <button onClick={save} disabled={!dirty || busy}
              className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-1 transition ${
                dirty
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.45)]'
                  : 'bg-arena-200/60 dark:bg-arena-700/40 text-arena-500 dark:text-neon-200/45 cursor-not-allowed'
              } disabled:cursor-not-allowed`}>
              {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
              Save permissions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function IdentityLockFieldsMatrix({ sa, onSaved, setErr, onDirtyChange }: {
  sa: Agent; onSaved: () => void; setErr: (s: string | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  type Lvl = 'none' | 'view' | 'edit';
  type Map = { username: Lvl; email: Lvl; avatar: Lvl; password: Lvl };
  const seedFromSa = (): Map => ({
    username: (sa.identity_locked_fields?.username as Lvl) || 'edit',
    email:    (sa.identity_locked_fields?.email    as Lvl) || 'edit',
    avatar:   (sa.identity_locked_fields?.avatar   as Lvl) || 'edit',
    password: (sa.identity_locked_fields?.password as Lvl) || 'edit',
  });
  const [m, setM]             = useState<Map>(seedFromSa);
  const [initial, setInitial] = useState<Map>(seedFromSa);
  const [busy, setBusy]       = useState(false);
  useEffect(() => {
    const seed = seedFromSa();
    setM(seed);
    setInitial(seed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sa.id]);
  const isRowChanged = (k: keyof Map) => m[k] !== initial[k];
  const dirty = (Object.keys(m) as (keyof Map)[]).some(isRowChanged);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);
  async function save() {
    setBusy(true); setErr(null);
    try {
      await adminApi.post(`/admin/agents/${sa.id}/identity-lock-fields`, { fields: m });
      setInitial(m);
      onSaved();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }
  function discard() { setM(initial); }
  const ROWS: Array<{ key: keyof Map; label: string; hint: string }> = [
    { key: 'username', label: 'Username', hint: 'Account login + display name' },
    { key: 'email',    label: 'Email',    hint: 'Used for login + password reset' },
    { key: 'avatar',   label: 'Avatar',   hint: 'Profile photo (upload + delete)' },
    { key: 'password', label: 'Password', hint: 'Change password from /profile' },
  ];
  return (
    <div className="space-y-2">
      <p className="text-xs text-arena-600 dark:text-neon-200/60 mb-1">
        Lock individual fields on the agent's own profile page.
      </p>
      <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 overflow-hidden">
        {ROWS.map((row) => {
          const changed = isRowChanged(row.key);
          return (
            <div key={row.key} className={`border-t ring-arena-200/60 dark:ring-arena-600/40 first:border-t-0 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 transition ${
              changed ? 'bg-amber-50/70 dark:bg-amber-500/[0.06] ring-1 ring-amber-300/60 dark:ring-amber-400/30 -mx-px' : ''
            }`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-arena-800 dark:text-neon-200/90 inline-flex items-center gap-2 flex-wrap">
                  {row.label}
                  {changed && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 dark:bg-amber-400/20 dark:text-amber-200">
                      Modified · was {initial[row.key]}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-arena-600 dark:text-neon-200/60">{row.hint}</div>
              </div>
              <div className="flex gap-1 self-stretch sm:self-auto">
                {(['none','view','edit'] as const).map((lvl) => {
                  const active = m[row.key] === lvl;
                  return (
                    <button key={lvl}
                      onClick={() => setM({ ...m, [row.key]: lvl })}
                      className={`flex-1 sm:flex-initial px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border transition ${
                        active
                          ? (lvl === 'edit' ? 'bg-emerald-600 border-emerald-700 text-white'
                            : lvl === 'view' ? 'bg-cyan-600 border-cyan-700 text-white'
                            : 'bg-violet-600 ring-1 ring-inset ring-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]')
                          : 'bg-white dark:bg-arena-800 ring-arena-200/60 dark:ring-arena-600/40 text-arena-400 dark:text-neon-200/70 hover:bg-arena-100/60 dark:hover:bg-arena-700/60'
                      }`}>
                      {lvl}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className={`sticky bottom-0 z-10 -mx-2 px-2 pt-2 pb-2 backdrop-blur-md transition ${
        dirty
          ? 'bg-amber-50/90 dark:bg-amber-500/[0.08] border-t border-amber-300/70 dark:border-amber-400/30'
          : 'bg-white/85 dark:bg-arena-900/85 border-t border-arena-200/40 dark:border-arena-700/40 backdrop-blur'
      }`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {dirty ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider
                             bg-amber-200 text-amber-900 dark:bg-amber-400/20 dark:text-amber-200
                             shadow-[0_0_12px_rgba(245,158,11,0.45)] dark:shadow-[0_0_14px_rgba(245,158,11,0.35)]">
              <Icon name="warning" size={12} filled /> Unsaved changes
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold
                             bg-arena-100/60 dark:bg-arena-800 text-arena-600 dark:text-neon-200/55">
              <Icon name="check_circle" size={12} /> All saved
            </span>
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={discard} disabled={!dirty || busy}
              className="px-3 py-2 rounded-xl text-[12px] font-semibold ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 text-arena-700 dark:text-neon-200/85 hover:bg-arena-50 dark:hover:bg-arena-700/60 disabled:opacity-40">
              Discard
            </button>
            <button onClick={save} disabled={!dirty || busy}
              className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-1 transition ${
                dirty
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.45)]'
                  : 'bg-arena-200/60 dark:bg-arena-700/40 text-arena-500 dark:text-neon-200/45 cursor-not-allowed'
              } disabled:cursor-not-allowed`}>
              {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
              Save field locks
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentLocationEditor({
  agent, onSaved, onError,
}: { agent: Agent; onSaved: (msg: string) => void; onError: (m: string) => void }) {
  const initial: GeoBag = {
    latitude:    agent.latitude    != null ? Number(agent.latitude)  : null,
    longitude:   agent.longitude   != null ? Number(agent.longitude) : null,
    state_id:    agent.state_id    ?? null,
    district_id: agent.district_id ?? null,
    area_id:     agent.area_id     ?? null,
    pincode_id:  agent.pincode_id  ?? null,
    pincode:     agent.pincode     ?? null,
    nation:      agent.nation || 'India',
  };
  const [geo, setGeo] = useState<GeoBag>(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setGeo({
      latitude:    agent.latitude    != null ? Number(agent.latitude)  : null,
      longitude:   agent.longitude   != null ? Number(agent.longitude) : null,
      state_id:    agent.state_id    ?? null,
      district_id: agent.district_id ?? null,
      area_id:     agent.area_id     ?? null,
      pincode_id:  agent.pincode_id  ?? null,
      pincode:     agent.pincode     ?? null,
      nation:      agent.nation || 'India',
    });
  }, [agent.latitude, agent.longitude, agent.state_id, agent.district_id, agent.area_id,
      agent.pincode_id, agent.pincode, agent.nation]);
  const dirty =
    (initial.latitude    ?? null) !== (geo.latitude    ?? null) ||
    (initial.longitude   ?? null) !== (geo.longitude   ?? null) ||
    (initial.state_id    ?? null) !== (geo.state_id    ?? null) ||
    (initial.district_id ?? null) !== (geo.district_id ?? null) ||
    (initial.area_id     ?? null) !== (geo.area_id     ?? null) ||
    (initial.pincode_id  ?? null) !== (geo.pincode_id  ?? null) ||
    (initial.nation      ?? 'India') !== (geo.nation   ?? 'India');
  async function save() {
    setBusy(true);
    try {
      const patch: Record<string, unknown> = {};
      const keys: (keyof GeoBag)[] = ['latitude','longitude','state_id','district_id','area_id','pincode_id','nation'];
      for (const k of keys) {
        const before = (initial[k] ?? null) as unknown;
        const now    = (geo[k]     ?? null) as unknown;
        if (before !== now) patch[k] = geo[k];
      }
      if (Object.keys(patch).length === 0) {
        onSaved('Nothing to save');
        return;
      }
      await adminApi.patch(`/admin/agents/${agent.id}`, patch);
      onSaved(`Location saved (${Object.keys(patch).length} field${Object.keys(patch).length === 1 ? '' : 's'})`);
    } catch (e) {
      onError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2">
          <Icon name="my_location" size={16} className="text-violet-600 dark:text-violet-300" />
          Geography
          {agent.geo_updated_at && (
            <span className="text-[11px] font-normal text-arena-500 dark:text-neon-200/60">
              · {new Date(agent.geo_updated_at).toLocaleDateString()}
            </span>
          )}
        </h3>
        <button
          onClick={save} disabled={!dirty || busy}
          className="px-3 py-1.5 rounded-lg text-[12px] font-bold uppercase tracking-wider
                     bg-violet-600 hover:bg-violet-500 active:bg-violet-700 text-white
                     shadow-[0_2px_10px_rgba(139,92,246,0.35)]
                     inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
          {busy ? <Icon name="progress_activity" size={12} className="animate-spin" /> : <Icon name="save" size={12} />}
          Save
        </button>
      </div>
      <LocationCapture
        key={agent.geo_updated_at ?? 'new'}
        value={geo}
        onChange={setGeo}
        mode="child"
        disabled={busy}
      />
      {!dirty && (
        <p className="text-[11px] text-arena-600 dark:text-neon-200/55 mt-2">
          Change state / district / area or pin code to enable Save.
        </p>
      )}
    </div>
  );
}
